/**
 * Civic economy (§6): the unstaffed Bank and the Guild Hall.
 *
 * Guild offers are deterministic per epoch and scale to what the settlement's
 * buildings can actually produce. Contributions escrow real graded goods;
 * completion is a manual, atomic owner action; abandoning refunds the exact
 * grades. Payouts derive from the storefront base-value table — one authority
 * for both systems, no drift.
 */
import { hashSeed } from "../rng.js";
import { SETTLEMENT_QUALITY_TIERS, legacyProfessionSpecialty,
  type SettlementQuality } from "./model.js";
import {
  BANK_CREDITS_MAX,
  BANK_DAILY_RATE_PERMYRIAD,
  BANK_DAY_MS,
  BANK_INTEREST_CAPS,
  PRODUCTION_RECIPES,
  addSettlementQualityGoods,
  removeSettlementGoodsAtLeast,
  settlementBankLevel,
  settlementCountAtLeast,
  settlementFacilityVariant,
  type SettlementGuildOrderV1,
  type SettlementStateV1,
} from "./production.js";
import { settlementBaseValue, settlementItemValue } from "./storefront.js";

export const SETTLEMENT_GUILD_INTERVAL_MS = 24 * 3_600_000;
export const GUILD_PREMIUM_CAP_PERMILLE = 400;
export const GUILD_LIAISON_BONUS_PERMILLE = 20;

export interface CivicOpResult {
  ok: boolean;
  state: SettlementStateV1;
  err?: string;
  /** completeGuildOrder: credits earned, granted atomically by the caller. */
  payout?: number;
}

// --- Bank (§6.1) ---

export interface SettlementBankReport {
  level: 0 | 1 | 2 | 3;
  credits: number;
  interestCap: number;
  /** Tomorrow's expected return at today's principal. */
  nextInterest: number;
  /** Deposited credits above the interest-bearing cap. */
  aboveCap: number;
}

export function settlementBankReport(state: SettlementStateV1): SettlementBankReport {
  const level = settlementBankLevel(state);
  const credits = state.bank?.credits ?? 0;
  const interestCap = level >= 1 ? BANK_INTEREST_CAPS[level as 1 | 2 | 3] : 0;
  return {
    level,
    credits,
    interestCap,
    nextInterest: level >= 1
      ? Math.floor(Math.min(credits, interestCap) * BANK_DAILY_RATE_PERMYRIAD / 10_000) : 0,
    aboveCap: Math.max(0, credits - interestCap),
  };
}

/** Move credits into the Bank. The caller has already advanced the state and
 * verified/debited the player side. */
export function bankDepositCredits(state: SettlementStateV1, n: number, nowMs: number): CivicOpResult {
  if (settlementBankLevel(state) < 1) return { ok: false, state, err: "NO BANK ESTABLISHED" };
  if (!Number.isInteger(n) || n < 1) return { ok: false, state, err: "INVALID CREDIT AMOUNT" };
  state.bank ??= { credits: 0, lastInterestDay: Math.floor(Math.max(0, nowMs) / BANK_DAY_MS) };
  if (state.bank.credits + n > BANK_CREDITS_MAX) return { ok: false, state, err: "BANK VAULT IS FULL" };
  state.bank.credits += n;
  return { ok: true, state };
}

export function bankWithdrawCredits(state: SettlementStateV1, n: number): CivicOpResult {
  if (!state.bank || !Number.isInteger(n) || n < 1 || state.bank.credits < n) {
    return { ok: false, state, err: "NOT ENOUGH BANKED CREDITS" };
  }
  state.bank.credits -= n;
  return { ok: true, state };
}

// --- Guild Hall (§6.2) ---

export interface SettlementGuildOffer {
  id: string;
  itemId: string;
  minQuality: SettlementQuality;
  quantity: number;
  premiumPermille: number;
  /** Payout preview at full completion, before the Liaison bonus. */
  payout: number;
}

function guildHallGrade(state: SettlementStateV1): number {
  let grade = 0;
  for (const facility of state.facilities) {
    if (settlementFacilityVariant(facility) === "guild-hall") grade = Math.max(grade, facility.grade);
  }
  return grade;
}

export function settlementGuildSlots(state: SettlementStateV1): { visible: number; pinned: number } {
  const grade = guildHallGrade(state);
  if (grade <= 0) return { visible: 0, pinned: 0 };
  return grade >= 2 ? { visible: 8, pinned: 4 } : { visible: 4, pinned: 2 };
}

/** Item ids this settlement can plausibly produce: outputs of recipes whose
 * building variant exists here. Offers never request the impossible (§6.2). */
function producibleGoods(state: SettlementStateV1): string[] {
  const variants = new Set(state.facilities.map((facility) => settlementFacilityVariant(facility)));
  const ids = new Set<string>();
  for (const recipe of PRODUCTION_RECIPES) {
    if (!recipe.variants.some((variant) => variants.has(variant))) continue;
    for (const stack of recipe.outputs) if (settlementBaseValue(stack.id) > 0) ids.add(stack.id);
  }
  return [...ids].sort();
}

export function settlementGuildEpoch(nowMs: number): number {
  return Math.floor(Math.max(0, nowMs) / SETTLEMENT_GUILD_INTERVAL_MS);
}

export function guildOrderPayout(order: Pick<SettlementGuildOrderV1,
  "itemId" | "minQuality" | "quantity" | "premiumPermille">, liaisonRank = 1): number {
  const base = settlementItemValue(order.itemId, order.minQuality, order.quantity);
  const bonus = Math.max(0, liaisonRank - 1) * GUILD_LIAISON_BONUS_PERMILLE;
  const premium = Math.min(GUILD_PREMIUM_CAP_PERMILLE, order.premiumPermille + bonus);
  return Math.ceil(base * (1000 + premium) / 1000);
}

/** The deterministic offer pool for the current epoch. Pinned orders are
 * excluded; unpinned offers simply rotate away at the next epoch. */
export function settlementGuildOffers(state: SettlementStateV1, nowMs: number): SettlementGuildOffer[] {
  const { visible } = settlementGuildSlots(state);
  if (visible === 0) return [];
  const goods = producibleGoods(state);
  if (goods.length === 0) return [];
  const epoch = settlementGuildEpoch(nowMs);
  const pinned = new Set((state.guildOrders ?? []).map((order) => order.id));
  const offers: SettlementGuildOffer[] = [];
  for (let slot = 0; slot < visible; slot++) {
    const id = `guild:${state.beaconId}:${epoch}:${slot}`;
    if (pinned.has(id)) continue;
    const seed = `${state.beaconId}:guild:${epoch}:${slot}`;
    const itemId = goods[hashSeed(seed) % goods.length]!;
    const value = Math.max(1, settlementBaseValue(itemId));
    // Ordinary orders target one-to-three login cycles of output: order value
    // lands around 300–900 credits regardless of the good's unit price.
    const scale = 300 + hashSeed(`${seed}:v`) % 601;
    const quantity = Math.max(2, Math.min(60, Math.round(scale / value)));
    const minQuality = SETTLEMENT_QUALITY_TIERS[[0, 0, 0, 1, 1, 2][hashSeed(`${seed}:q`) % 6]!]!;
    const premiumPermille = 100 + hashSeed(`${seed}:p`) % 201;
    offers.push({
      id, itemId, minQuality, quantity, premiumPermille,
      payout: guildOrderPayout({ itemId, minQuality, quantity, premiumPermille }),
    });
  }
  return offers;
}

export function pinGuildOrder(state: SettlementStateV1, offerId: string, nowMs: number): CivicOpResult {
  const { pinned } = settlementGuildSlots(state);
  if (pinned === 0) return { ok: false, state, err: "NO GUILD HALL ESTABLISHED" };
  if ((state.guildOrders ?? []).length >= pinned) return { ok: false, state, err: "PINNED ORDER SLOTS ARE FULL" };
  const offer = settlementGuildOffers(state, nowMs).find((entry) => entry.id === offerId);
  if (!offer) return { ok: false, state, err: "OFFER HAS ROTATED AWAY" };
  (state.guildOrders ??= []).push({
    id: offer.id, itemId: offer.itemId, minQuality: offer.minQuality,
    quantity: offer.quantity, premiumPermille: offer.premiumPermille,
    contributed: 0, escrow: [],
  });
  return { ok: true, state };
}

/** Contribute as much qualifying stock as the order still needs. Goods enter
 * protected escrow and cannot be consumed elsewhere (§6.2). */
export function contributeGuildOrder(state: SettlementStateV1, orderId: string): CivicOpResult {
  const order = (state.guildOrders ?? []).find((entry) => entry.id === orderId);
  if (!order) return { ok: false, state, err: "ORDER NOT FOUND" };
  const needed = order.quantity - order.contributed;
  const available = settlementCountAtLeast(state, order.itemId, order.minQuality);
  const take = Math.min(needed, available);
  if (take < 1) return { ok: false, state, err: needed < 1 ? "ORDER IS ALREADY FILLED" : "NO QUALIFYING GOODS IN STORAGE" };
  const rows = removeSettlementGoodsAtLeast(state, order.itemId, take, order.minQuality);
  if (!rows) return { ok: false, state, err: "NO QUALIFYING GOODS IN STORAGE" };
  order.escrow.push(...rows);
  order.contributed += take;
  return { ok: true, state };
}

/** The Liaison's negotiation rank: the best-ranked liaison in town. */
export function settlementLiaisonRank(state: SettlementStateV1): number {
  let rank = 1;
  for (const resident of state.residents) {
    if (legacyProfessionSpecialty(resident.profession) === "liaison") rank = Math.max(rank, resident.rank);
  }
  return rank;
}

/** Manual completion (§6.2): the payoff is a return moment, never invisible.
 * The caller grants `payout` credits atomically with persistence. */
export function completeGuildOrder(state: SettlementStateV1, orderId: string): CivicOpResult {
  const order = (state.guildOrders ?? []).find((entry) => entry.id === orderId);
  if (!order) return { ok: false, state, err: "ORDER NOT FOUND" };
  if (order.contributed < order.quantity) return { ok: false, state, err: "ORDER IS NOT FILLED YET" };
  state.guildOrders = state.guildOrders!.filter((entry) => entry.id !== orderId);
  return { ok: true, state, payout: guildOrderPayout(order, settlementLiaisonRank(state)) };
}

/** Abandoning returns every escrowed unit at its exact grade (§6.2). */
export function abandonGuildOrder(state: SettlementStateV1, orderId: string): CivicOpResult {
  const order = (state.guildOrders ?? []).find((entry) => entry.id === orderId);
  if (!order) return { ok: false, state, err: "ORDER NOT FOUND" };
  for (const row of order.escrow) addSettlementQualityGoods(state, row.id, row.n, row.quality);
  state.guildOrders = state.guildOrders!.filter((entry) => entry.id !== orderId);
  return { ok: true, state };
}
